import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  Box,
  CalendarDays,
  Check,
  ChevronRight,
  FileText,
  House,
  Layers,
  Link2,
  ListChecks,
  Package,
  Pause,
  Play,
  Rocket,
  Settings2,
  Sparkles,
  Target,
  TrendingUp,
  UserRound,
  Users,
} from 'lucide-react';
import './HomeStrategyDemo.css';

const examples = [
  {
    id: 'revenue',
    label: 'Grow revenue',
    icon: TrendingUp,
    objective: 'Build a repeatable growth engine',
    outcome: 'Increase qualified pipeline by 25% in 90 days.',
    context: [
      'B2B studio · 12-person team',
      'Goal: predictable revenue',
      'Strength: trusted client relationships',
    ],
    owner: 'Jamie · Growth lead',
    approaches: [
      {
        strategy: 'Grow through client referrals',
        rationale: 'Use strong client relationships to reach more of the right customers.',
        project: 'Launch a referral programme',
        tasks: [
          'Define the referral offer',
          'Assign a programme owner',
          'Invite your first five clients',
        ],
      },
      {
        strategy: 'Build a partner network',
        rationale: 'Reach new customers through partners who already serve your ideal clients.',
        project: 'Pilot three growth partnerships',
        tasks: [
          'Shortlist complementary partners',
          'Create a shared offer',
          'Launch the first partner pilot',
        ],
      },
    ],
  },
  {
    id: 'product',
    label: 'Launch a product',
    icon: Package,
    objective: 'Bring a new offer to market',
    outcome: 'Validate the new offer with 10 paying customers in 90 days.',
    context: [
      'B2B studio · 12-person team',
      'Goal: validate a new offer',
      'Strength: an engaged client base',
    ],
    owner: 'Alex · Product lead',
    approaches: [
      {
        strategy: 'Start with customer validation',
        rationale: 'Test real customer demand before committing to a full launch.',
        project: 'Run a paid beta programme',
        tasks: [
          'Interview five target customers',
          'Define the beta offer',
          'Invite your first beta customers',
        ],
      },
      {
        strategy: 'Co-create with existing clients',
        rationale: 'Shape the new offer with clients who already know and trust your team.',
        project: 'Launch a client design partnership',
        tasks: [
          'Select three design partners',
          'Agree on pilot success measures',
          'Deliver the first pilot',
        ],
      },
    ],
  },
  {
    id: 'operations',
    label: 'Scale operations',
    icon: Settings2,
    objective: 'Make great delivery repeatable',
    outcome: 'Reduce project handover time by 30% in 90 days.',
    context: [
      'B2B studio · 12-person team',
      'Goal: smoother project delivery',
      'Strength: experienced team leads',
    ],
    owner: 'Sam · Operations lead',
    approaches: [
      {
        strategy: 'Standardise project handovers',
        rationale: 'Give every team a clear, shared process for moving work forward.',
        project: 'Pilot a shared delivery playbook',
        tasks: [
          'Map the current handover process',
          'Create a handover checklist',
          'Trial it on one live project',
        ],
      },
      {
        strategy: 'Remove delivery bottlenecks',
        rationale: 'Focus improvement on the points where your team loses the most time.',
        project: 'Run a workflow improvement sprint',
        tasks: [
          'Identify the biggest bottleneck',
          'Assign an improvement owner',
          'Measure the first trial',
        ],
      },
    ],
  },
];

const stages = [
  {
    id: 'objective',
    label: 'Objective',
    icon: Target,
    heading: 'Clear direction for every decision',
    description:
      'Start with a measurable outcome. Every strategy, project and task connects back to it.',
    connection: 'Sets the direction for your strategy',
  },
  {
    id: 'strategy',
    label: 'Strategy',
    icon: FileText,
    heading: 'A focused way to reach your goal',
    description: 'Choose where to focus using your goals, strengths and company context.',
    connection: 'Connects your objective to a practical project',
  },
  {
    id: 'project',
    label: 'Project',
    icon: Box,
    heading: 'Give your strategy an owner and a plan',
    description:
      'Turn your chosen approach into a defined project, with ownership and clear next steps.',
    connection: 'Turns your strategic choice into work',
  },
  {
    id: 'tasks',
    label: 'Tasks',
    icon: ListChecks,
    heading: 'Small steps. Visible progress.',
    description: 'Tick a task below. Watch linked work progress update across the plan.',
    connection: 'Task progress flows through every linked step',
  },
];

const workspaceSections = [
  { label: 'Foundation', icon: House },
  { label: 'Assessments', icon: BarChart3 },
  { label: 'Strategy', icon: Target },
  { label: 'People', icon: Users },
  { label: 'Execution', icon: Rocket },
  { label: 'Reports', icon: FileText },
];

export default function HomeStrategyDemo() {
  const [exampleIndex, setExampleIndex] = useState(0);
  const [approachIndex, setApproachIndex] = useState(0);
  const [activeStage, setActiveStage] = useState(0);
  const [completedTasks, setCompletedTasks] = useState<number[]>([]);
  const [playing, setPlaying] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [announcement, setAnnouncement] = useState('');
  const demoRef = useRef<HTMLElement>(null);
  const example = examples[exampleIndex];
  const approach = example.approaches[approachIndex];
  const stage = stages[activeStage];
  const StageIcon = stage.icon;
  const progress = Math.round((completedTasks.length / approach.tasks.length) * 100);
  const touring = playing && inView && pageVisible && !reducedMotion;

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const compact = window.matchMedia('(max-width: 700px)');
    const syncMotion = () => {
      setReducedMotion(media.matches);
      if (media.matches) setPlaying(false);
    };
    syncMotion();
    const pauseCompactTour = () => {
      if (compact.matches) setPlaying(false);
    };
    setPlaying(!media.matches && !compact.matches);
    media.addEventListener('change', syncMotion);
    compact.addEventListener('change', pauseCompactTour);
    return () => {
      media.removeEventListener('change', syncMotion);
      compact.removeEventListener('change', pauseCompactTour);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.25,
    });
    if (demoRef.current) observer.observe(demoRef.current);
    const syncVisibility = () => setPageVisible(!document.hidden);
    syncVisibility();
    document.addEventListener('visibilitychange', syncVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', syncVisibility);
    };
  }, []);

  useEffect(() => {
    if (!touring) return;
    const timer = window.setInterval(
      () => setActiveStage((current) => (current + 1) % stages.length),
      5000
    );
    return () => window.clearInterval(timer);
  }, [touring]);

  const selectStage = (index: number) => {
    setPlaying(false);
    setActiveStage(index);
  };

  const chooseExample = (index: number) => {
    setPlaying(false);
    if (index === exampleIndex) return;
    setExampleIndex(index);
    setApproachIndex(0);
    setCompletedTasks([]);
    setActiveStage(0);
    setAnnouncement(
      `${examples[index].label} example selected. Its objective, strategy, project and tasks are now connected.`
    );
  };

  const tryAnotherApproach = () => {
    const nextApproach = (approachIndex + 1) % example.approaches.length;
    setPlaying(false);
    setApproachIndex(nextApproach);
    setCompletedTasks([]);
    setActiveStage(1);
    setAnnouncement(
      `Alternative example: ${example.approaches[nextApproach].strategy}. The linked project and tasks have updated. Task progress starts fresh for this approach.`
    );
  };

  const toggleTask = (index: number) => {
    const nextTasks = completedTasks.includes(index)
      ? completedTasks.filter((task) => task !== index)
      : [...completedTasks, index];
    setPlaying(false);
    setCompletedTasks(nextTasks);
    setAnnouncement(
      `${nextTasks.length} of ${approach.tasks.length} tasks complete. Linked work is ${Math.round((nextTasks.length / approach.tasks.length) * 100)} percent complete.`
    );
  };

  const titles = [
    example.objective,
    approach.strategy,
    approach.project,
    completedTasks.length === approach.tasks.length
      ? 'Every task completed'
      : approach.tasks.find((_, index) => !completedTasks.includes(index)),
  ];

  return (
    <section
      id="home-demo"
      className="strategy-demo"
      aria-label="Interactive strategy example"
      data-playing={touring}
      ref={demoRef}
      onFocusCapture={(event) => {
        if (!(event.target as HTMLElement).closest('.demo-tour-toggle')) setPlaying(false);
      }}
    >
      <div className="demo-workspace-bar">
        <div className="demo-brand">
          <span className="demo-brand-icon">
            <Layers size={20} aria-hidden="true" />
          </span>
          <strong>Northstar Studio</strong>
        </div>
        <span className="demo-example-badge">
          <span />
          Interactive example
        </span>
        <span className="demo-workspace-label">Sample workspace</span>
      </div>
      <div className="demo-workspace">
        <aside className="demo-sidebar" aria-label="Example workspace sections">
          {workspaceSections.map(({ label, icon: Icon }) => (
            <span
              key={label}
              className={`demo-sidebar-item${label === 'Strategy' ? ' is-current' : ''}`}
            >
              <Icon size={20} aria-hidden="true" />
              {label}
            </span>
          ))}
          <div className="demo-sidebar-note">
            <Sparkles size={20} aria-hidden="true" />
            <strong>A plan that connects.</strong>
            <p>From the big picture to the next task.</p>
          </div>
        </aside>
        <div className="demo-main">
          <div className="demo-heading">
            <div>
              <h2>From direction to delivery</h2>
              <p>Choose a goal. See how it becomes a plan.</p>
            </div>
            <button
              type="button"
              className="demo-tour-toggle"
              disabled={reducedMotion}
              onClick={() => setPlaying((current) => !current)}
              aria-label={
                reducedMotion
                  ? 'Automatic tour disabled for reduced motion'
                  : playing
                    ? 'Pause automatic tour'
                    : 'Play automatic tour'
              }
            >
              {playing ? (
                <Pause size={14} aria-hidden="true" />
              ) : (
                <Play size={14} aria-hidden="true" />
              )}
              <span>{reducedMotion ? 'Manual tour' : playing ? 'Pause tour' : 'Play tour'}</span>
            </button>
          </div>
          <div className="demo-goals" role="group" aria-label="Choose an example goal">
            <span className="demo-goals-label">Your goal</span>
            {examples.map(({ id, label, icon: Icon }, index) => (
              <button
                key={id}
                type="button"
                aria-pressed={index === exampleIndex}
                onClick={() => chooseExample(index)}
              >
                <Icon size={15} aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
          <div className="demo-chain" role="group" aria-label="Explore the connected plan">
            {stages.map(({ id, label, icon: Icon }, index) => (
              <div
                key={id}
                className={`demo-step-wrap${index === activeStage ? ' is-active' : ''}`}
              >
                <button
                  type="button"
                  className={`demo-step demo-step-${id}${index === activeStage ? ' is-active' : ''}`}
                  aria-pressed={index === activeStage}
                  aria-controls="demo-stage-detail"
                  aria-label={`Explore ${label.toLowerCase()}: ${titles[index]}. ${progress}% linked task progress`}
                  onClick={() => selectStage(index)}
                >
                  <span className="demo-step-icon">
                    <Icon size={24} strokeWidth={1.8} aria-hidden="true" />
                  </span>
                  <span className="demo-step-label">{label}</span>
                  <strong className="demo-step-title">{titles[index]}</strong>
                  <span className="demo-step-meta">
                    {index === 0
                      ? '90-day business goal'
                      : index === 1
                        ? 'Linked to your objective'
                        : index === 2
                          ? example.owner
                          : `${completedTasks.length} of ${approach.tasks.length} tasks complete`}
                  </span>
                  <span
                    className="demo-progress-track"
                    role="progressbar"
                    aria-label={`${label} linked task progress`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progress}
                  >
                    <span style={{ width: `${progress}%` }} />
                  </span>
                </button>
                {index < stages.length - 1 && (
                  <ChevronRight className="demo-connector" size={19} aria-hidden="true" />
                )}
              </div>
            ))}
          </div>
          <p className="demo-mobile-summary">
            <strong>{stage.label}</strong>
            <span>{titles[activeStage]}</span>
          </p>
          <div className="demo-bottom">
            <section
              className="demo-detail"
              id="demo-stage-detail"
              aria-label={`${stage.label} details`}
            >
              <div className="demo-detail-heading">
                <span className="demo-detail-icon">
                  <StageIcon size={20} aria-hidden="true" />
                </span>
                <div>
                  <span className="demo-detail-kicker">
                    Step {activeStage + 1} of 4 · {stage.label}
                  </span>
                  <h3>{stage.heading}</h3>
                </div>
              </div>
              <p className="demo-detail-description">{stage.description}</p>
              <div
                className="demo-detail-content"
                key={`${example.id}-${approachIndex}-${stage.id}`}
              >
                {activeStage === 0 && (
                  <div className="demo-outcome">
                    <span>Success looks like</span>
                    <strong>{example.outcome}</strong>
                  </div>
                )}
                {activeStage === 1 && (
                  <div className="demo-outcome">
                    <span>Why this approach</span>
                    <strong>{approach.rationale}</strong>
                  </div>
                )}
                {activeStage === 2 && (
                  <div className="demo-project-meta">
                    <span>
                      <UserRound size={16} aria-hidden="true" />
                      {example.owner}
                    </span>
                    <span>
                      <CalendarDays size={16} aria-hidden="true" />
                      Next 30 days
                    </span>
                  </div>
                )}
                {activeStage === 3 && (
                  <div className="demo-task-list">
                    {approach.tasks.map((task, index) => (
                      <label
                        key={task}
                        className={`demo-task${completedTasks.includes(index) ? ' is-complete' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={completedTasks.includes(index)}
                          onChange={() => toggleTask(index)}
                        />
                        <span>{task}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="demo-detail-footer">
                <span className="demo-connection-note">
                  <Link2 size={14} aria-hidden="true" />
                  {stage.connection}
                </span>
                <button
                  type="button"
                  className="demo-next-step"
                  onClick={() => selectStage((activeStage + 1) % stages.length)}
                >
                  {activeStage === 3
                    ? 'Back to objective'
                    : `Explore ${stages[activeStage + 1].label.toLowerCase()}`}
                  <ArrowRight size={15} aria-hidden="true" />
                </button>
              </div>
            </section>
            <aside className="demo-assistant" aria-label="Example company context">
              <div className="demo-assistant-heading">
                <Sparkles size={20} aria-hidden="true" />
                <h3>Context makes it smarter.</h3>
              </div>
              <p>A useful plan starts with your business.</p>
              <ul className="demo-context-list">
                {example.context.map((item) => (
                  <li key={item}>
                    <Check size={14} aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <button type="button" className="demo-generate" onClick={tryAnotherApproach}>
                <Sparkles size={16} aria-hidden="true" />
                Try another approach
                <ArrowRight size={15} aria-hidden="true" />
              </button>
              <span className="demo-assistant-note">
                Prepared examples of AI-assisted planning.
              </span>
            </aside>
          </div>
          <div className="demo-footer">
            <span>
              <span className="demo-live-dot" />
              Linked work: {progress}% complete
            </span>
            <span className="demo-footer-hint">
              {progress === 100
                ? 'Tasks done. Next, measure your business outcome.'
                : 'Try completing a task to see progress flow.'}
            </span>
          </div>
        </div>
      </div>
      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>
    </section>
  );
}
